import { useState, useEffect } from 'react';
import type { LeakType, Severity, Archetype } from '../page';

interface ResultSectionProps {
  leakType: LeakType;
  severity: Severity;
  archetype: Archetype;
  severityTier: number;
  onLavooClick: () => void;
}

const archetypeContent: Record<Archetype, {
  headline: string;
  subheadline: string;
  explanation: string;
  ctaText: string;
}> = {
  'Reactive Operator': {
    headline: "You're Operating in Reactive Mode.",
    subheadline: "Your business isn't slow. Your decisions are.",
    explanation: "You respond faster than you decide. Messages, urgency, and external demands are shaping your direction. Over time, this compounds into invisible drift.",
    ctaText: "Regain Strategic Control"
  },
  'Fragmented Focus': {
    headline: "You Have Momentum — But It's Leaking.",
    subheadline: "You're productive. Just not aligned.",
    explanation: "You move. You build. You execute. But your decisions lack structural gravity. The result isn't failure. It's dilution.",
    ctaText: "Align My Decisions"
  },
  'Tool Overload': {
    headline: "Complexity Is Replacing Clarity.",
    subheadline: "Your stack is doing more than your system.",
    explanation: "Tools should reduce cognitive load. Instead, they're multiplying decision paths. Each added layer introduces friction.",
    ctaText: "Simplify My System"
  },
  'Decision Drift': {
    headline: "Your Direction Isn't Broken. It's Unstructured.",
    subheadline: "Ambition without prioritisation compounds slowly.",
    explanation: "You're not lacking effort. You're lacking forced clarity. When everything feels important, nothing becomes decisive.",
    ctaText: "Clarify My Next Move"
  }
};

const timeLeakRanges: Record<number, { weekly: string; monthly: string; quarterly: string }> = {
  1: {
    weekly: '8–12 hours',
    monthly: '32–48 hours',
    quarterly: '1–1.5 working weeks'
  },
  2: {
    weekly: '12–18 hours',
    monthly: '48–72 hours',
    quarterly: '1.5–2 working weeks'
  },
  3: {
    weekly: '18–25 hours',
    monthly: '72–100 hours',
    quarterly: '2–2.5 working weeks'
  },
  4: {
    weekly: '25–35 hours',
    monthly: '100–140 hours',
    quarterly: '2.5–3.5 working weeks'
  }
};

const emotionalReinforcement: Record<Archetype, string> = {
  'Reactive Operator': "High performers don't just work harder. They operate inside systems that reduce decision friction.",
  'Fragmented Focus': "High performers don't just work harder. They operate inside systems that reduce decision friction.",
  'Tool Overload': "Eliminating leaks isn't about fewer tools. It's about a higher-order decision layer.",
  'Decision Drift': "Founders who eliminate this don't add more effort. They operate inside systems that make the next move obvious."
};

const severityStages = ['Signal Detected', 'Structural Friction', 'Momentum Erosion', 'Compounding Drift'];

const consequenceLines: Record<number, string> = {
  1: 'Growth is slowing quietly.',
  2: 'Momentum is leaking consistently.',
  3: 'Your system is costing you strategic time.',
  4: 'Compounding drift is already shaping outcomes.'
};

export default function ResultSection({ leakType, severity, archetype, severityTier, onLavooClick }: ResultSectionProps) {
  const [showSimulation, setShowSimulation] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const [animatingStage, setAnimatingStage] = useState(0);
  const [meterAnimationComplete, setMeterAnimationComplete] = useState(false);
  const content = archetypeContent[archetype];
  const timeLeaks = timeLeakRanges[severityTier];

  useEffect(() => {
    const timer1 = setTimeout(() => {
      setShowSimulation(false);
    }, 1500);

    const timer2 = setTimeout(() => {
      setShowContent(true);
    }, 1600);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  useEffect(() => {
    if (showContent && !meterAnimationComplete) {
      let currentStage = 0;
      const interval = setInterval(() => {
        if (currentStage < severityTier) {
          setAnimatingStage(currentStage + 1);
          currentStage++;
        } else {
          setMeterAnimationComplete(true);
          clearInterval(interval);
        }
      }, 200);

      return () => clearInterval(interval);
    }
  }, [showContent, severityTier, meterAnimationComplete]);

  if (showSimulation) {
    return (
      <section className="flex items-center justify-center min-h-screen px-6 py-12">
        <div className="text-center">
          <p className="text-xl text-gray-600 mb-4 animate-pulse">
            Running clarity simulation…
          </p>
          <div className="w-32 h-0.5 bg-[#e87a02] mx-auto animate-[pulse_1s_ease-in-out_infinite]" />
        </div>
      </section>
    );
  }

  return (
    <section className="flex items-center justify-center min-h-screen px-6 py-12">
      <div className={`max-w-2xl w-full transition-all duration-700 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            {content.headline}
          </h1>
          
          <p className="text-xl text-gray-600 mb-6">
            {content.subheadline}
          </p>
          
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            {content.explanation}
          </p>

          <p className="text-base text-gray-600 italic">
            {emotionalReinforcement[archetype]}
          </p>
        </div>

        <div className="mb-12">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Leak Risk</h3>
          
          <div className="relative mb-6">
            <div className="flex justify-between mb-3">
              {severityStages.map((stage, index) => {
                const isActive = index + 1 === severityTier;
                const hasAnimated = animatingStage >= index + 1;
                
                return (
                  <span 
                    key={stage}
                    className={`text-xs tracking-wide transition-all duration-300 ${
                      isActive 
                        ? 'font-semibold text-gray-900' 
                        : hasAnimated 
                        ? 'text-gray-500' 
                        : 'text-gray-300'
                    }`}
                  >
                    {stage}
                  </span>
                );
              })}
            </div>
            
            <div className="flex gap-1.5 h-4 mb-4">
              {severityStages.map((stage, index) => {
                const isActive = index + 1 === severityTier;
                const hasAnimated = animatingStage >= index + 1;
                
                return (
                  <div
                    key={stage}
                    className={`flex-1 transition-all duration-500 ${
                      hasAnimated 
                        ? isActive 
                          ? 'bg-[#e87a02] shadow-[0_0_12px_rgba(232,122,2,0.3)]' 
                          : 'bg-[#e87a02]' 
                        : 'bg-gray-200'
                    } ${isActive && meterAnimationComplete ? 'animate-[pulse_1s_ease-in-out_1]' : ''}`}
                    style={{ transitionDelay: `${index * 200}ms` }}
                  />
                );
              })}
            </div>

            {meterAnimationComplete && (
              <div className={`transition-all duration-500 ${meterAnimationComplete ? 'opacity-100' : 'opacity-0'}`}>
                <div className="mb-4 p-4 bg-[#e87a02]/8 rounded-lg">
                  <p className="text-base text-gray-900 mb-2">
                    <span className="font-bold">Estimated Weekly Momentum Loss:</span> {timeLeaks.weekly}
                  </p>
                  <p className="text-base text-gray-700 font-medium mb-1">
                    That's roughly {timeLeaks.monthly} per month.
                  </p>
                  <p className="text-sm text-gray-600">
                    Nearly {timeLeaks.quarterly} per quarter.
                  </p>
                </div>

                <p className="text-base text-gray-600">
                  {consequenceLines[severityTier]}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="w-full h-px bg-gray-200 my-12" />

        <div className="mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">
            Close the Leak Before It Compounds
          </h3>
          
          <p className="text-lg text-gray-700 mb-6 leading-relaxed">
            This isn't a motivation problem. It's a clarity system gap. The pattern you're experiencing has a structural solution.
          </p>

          <p className="text-lg text-gray-700 mb-2 leading-relaxed">
            Founders who eliminate this don't work longer hours.
          </p>

          <p className="text-lg text-gray-700 mb-8 leading-relaxed">
            They operate with a different decision system.
          </p>
        </div>

        <p className="text-sm text-gray-500 text-center mb-3">
          Access is currently released in controlled waves.
        </p>

        <div className="mb-4">
          <button
            onClick={onLavooClick}
            className="w-full bg-gray-900 text-white px-8 py-5 rounded-lg text-lg font-semibold hover:bg-gray-800 hover:scale-[1.02] transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            {content.ctaText}
          </button>
        </div>
      </div>
    </section>
  );
}